import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

import {
  systemFields,
  zFaq,
  zTransliterations,
} from "@/lib/zod/show-fragments";

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------

const FaqItemSchema = z.object({
  question: z.string(),
  answer: z.string(),
});

// ---------------------------------------------------------------------------
// Component enrichments — tracks which entity IDs contributed
// ---------------------------------------------------------------------------

// Keys are the backend's (`EventComponentEnrichmentsDbDto`), not shorthand:
// `enrichedArtistIds` / `enrichedGenreIds` are `required` on that DTO, so a
// parse that renames or drops them turns Approve & Save into a 400
// DeserializationError. Curators never edit this block — it only has to survive
// the round-trip intact.
const ComponentEnrichmentsSchema = z
  .object({
    enrichedArtistIds: z.array(z.string()).nullish(),
    enrichedGenreIds: z.array(z.string()).nullish(),
    enrichedVenueId: z.string().nullish(),
  })
  .passthrough();

// ---------------------------------------------------------------------------
// 23 event features (from MOO-3887 spec)
//
// Every key of `EventFeaturesDbDto` must be listed. The edit dialog replaces
// `features` wholesale on submit, so an unlisted feature is not merely dropped
// on load — it is erased from the stored enrichment. `.passthrough()` cannot
// save it: AutoForm only ever returns the fields it rendered.
// ---------------------------------------------------------------------------

const eventFeaturesSchema = z.object({
  // 9 venue-only
  capacity: z.coerce.number().min(0).max(1).nullish(),
  outdoor: z.coerce.number().min(0).max(1).nullish(),
  nature: z.coerce.number().min(0).max(1).nullish(),
  view: z.coerce.number().min(0).max(1).nullish(),
  seating: z.coerce.number().min(0).max(1).nullish(),
  dining: z.coerce.number().min(0).max(1).nullish(),
  rawness: z.coerce.number().min(0).max(1).nullish(),
  undergroundness: z.coerce.number().min(0).max(1).nullish(),
  selectivity: z.coerce.number().min(0).max(1).nullish(),
  // 8 lineup-only
  polish: z.coerce.number().min(0).max(1).nullish(),
  nostalgia: z.coerce.number().min(0).max(1).nullish(),
  locality: z.coerce.number().min(0).max(1).nullish(),
  fashion: z.coerce.number().min(0).max(1).nullish(),
  commerciality: z.coerce.number().min(0).max(1).nullish(),
  tempo: z.coerce.number().min(0).max(1).nullish(),
  vocality: z.coerce.number().min(0).max(1).nullish(),
  organicness: z.coerce.number().min(0).max(1).nullish(),
  // 6 blended (lineup + venue)
  energy: z.coerce.number().min(0).max(1).nullish(),
  age: z.coerce.number().min(0).max(1).nullish(),
  queerness: z.coerce.number().min(0).max(1).nullish(),
  danceability: z.coerce.number().min(0).max(1).nullish(),
  valence: z.coerce.number().min(0).max(1).nullish(),
  grit: z.coerce.number().min(0).max(1).nullish(),
});

// ---------------------------------------------------------------------------
// SUBMIT schema — what PUT /events/enrich accepts.
//
// Passthrough: Approve & Save PUTs this object back at /events/enrich, which
// full-replaces the stored enrichment, so any key not modelled here is
// destroyed on the first approval. Pipeline-written keys (`taskName`,
// `enrichmentSources`, …) must survive untouched — enumerating them would
// re-break on the next key the enrichment flow adds. Event was the first entity
// to get this treatment (a3ea7ae); artist, venue and geodata have it too, and
// the lenient READ half is below.
// ---------------------------------------------------------------------------

export const eventEnrichmentSubmitSchema = z
  .object({
    id: z.string(),
    // Genkit flow outputs
    normalizedTitle: z.string().nullish(),
    // Full <title>. Null means "use the app's title formula".
    seoTitle: z.string().nullish(),
    shortDescription: z
      .string()
      .nullish()
      .superRefine(fieldConfig({ fieldType: "textarea" })),
    aliases: z.array(z.string()).nullish(),
    keywords: z.array(z.string()).nullish(),
    relatedSearches: z.array(z.string()).nullish(),
    transliterations: zTransliterations.nullish(),
    faq: z.array(FaqItemSchema).nullish(),
    // Computed features
    features: eventFeaturesSchema.nullish(),
    // Which components contributed
    componentEnrichments: ComponentEnrichmentsSchema.nullish(),
    // Timestamps
    updatedAt: z.string().datetime({ offset: true }).nullish(),
    updatedBy: z.string().nullish(),
    createdAt: z.string().datetime({ offset: true }).nullish(),
    createdBy: z.string().nullish(),
  })
  .passthrough();

/**
 * READ schema — lenient, for rendering whatever the API returned.
 *
 * Every field nullish and `.passthrough()` throughout, consumed through
 * `parseRead()` from `@/lib/zod/read-schema`. The submit schema is already
 * almost all-nullish, so this differs mainly in `id` and in not caring whether
 * the timestamps are RFC-3339 — a stored enrichment written before a format
 * change must still render.
 */
export const eventEnrichmentReadSchema = z
  .object({
    id: z.string().nullish(),
    normalizedTitle: z.string().nullish(),
    seoTitle: z.string().nullish(),
    shortDescription: z.string().nullish(),
    aliases: z.array(z.string()).nullish(),
    keywords: z.array(z.string()).nullish(),
    relatedSearches: z.array(z.string()).nullish(),
    transliterations: zTransliterations.nullish(),
    faq: z.array(zFaq).nullish(),
    features: eventFeaturesSchema.passthrough().nullish(),
    componentEnrichments: ComponentEnrichmentsSchema.nullish(),
    ...systemFields,
  })
  .passthrough();

export const eventEditableEnrichmentSchema = eventEnrichmentSubmitSchema.omit({
  id: true,
  componentEnrichments: true,
  updatedAt: true,
  updatedBy: true,
  createdAt: true,
  createdBy: true,
});

export type EventAIEnrichmentType = z.infer<typeof eventEnrichmentSubmitSchema>;
export type EventEnrichmentRead = z.infer<typeof eventEnrichmentReadSchema>;
/** @deprecated Prefer the explicit read/submit pair. Kept so existing importers compile. */
export { eventEnrichmentSubmitSchema as eventAIEnrichmentSchema };
export type EventEditableEnrichmentType = z.infer<
  typeof eventEditableEnrichmentSchema
>;
export type EventFeaturesType = z.infer<typeof eventFeaturesSchema>;
export type FaqItem = z.infer<typeof FaqItemSchema>;
export { eventFeaturesSchema };
